import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import SiteLayout from "@/components/feature/SiteLayout";
import { useSeo } from "@/hooks/useSeo";
import { searchMedia, sortItems } from "@/hooks/useMediaQuery";
import { typeLabels } from "@/mocks/filters";
import { mediaList, type ContentType } from "@/mocks/media";

const typeFilters: { key: string; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "variety", label: "综艺" },
];

const hotKeywords = ["悬疑", "治愈", "科幻", "国风动画", "刑侦", "美食", "露营", "访谈"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [typeFilter, setTypeFilter] = useState<string>("all");

  useEffect(() => {
    setInput(keyword);
    setTypeFilter("all");
  }, [keyword]);

  useSeo(
    keyword ? `搜索结果：“${keyword}” - 97视频` : "影视搜索 - 97视频",
    keyword
      ? `97视频为您找到与“${keyword}”相关的电影、电视剧与综艺作品，可按类型进一步筛选，查看简介、主演与上映年份。`
      : "在97视频搜索电影、电视剧、综艺标题或主演，快速定位想看的影视作品。",
    keyword ? `${keyword},影视搜索,97视频搜索` : "影视搜索,97视频,电影搜索,电视剧搜索",
  );

  const results = useMemo(() => {
    if (!keyword.trim()) return [];
    const matched = searchMedia(keyword);
    const filtered = typeFilter === "all" ? matched : matched.filter((item) => item.type === typeFilter);
    return sortItems(filtered, "heat");
  }, [keyword, typeFilter]);

  const hotPicks = sortItems(mediaList, "heat").slice(0, 6);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const word = input.trim();
    setParams(word ? { q: word } : {});
  };

  const counts = useMemo(() => {
    const matched = keyword.trim() ? searchMedia(keyword) : [];
    return {
      all: matched.length,
      movie: matched.filter((i) => i.type === "movie").length,
      tv: matched.filter((i) => i.type === "tv").length,
      variety: matched.filter((i) => i.type === "variety").length,
    } as Record<string, number>;
  }, [keyword]);

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100/60">
        <div className="w-full px-4 py-8 md:px-6 md:py-10">
          <nav className="flex items-center gap-1.5 font-label text-[11px] text-foreground-500">
            <Link to="/" className="transition-colors hover:text-accent-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line flex h-3.5 w-3.5 items-center justify-center" />
            <span className="text-foreground-800">搜索</span>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
            {keyword ? `搜索结果：“${keyword}”` : "搜索影视内容"}
          </h1>
          <p className="mt-2 text-[13px] text-foreground-600">
            {keyword
              ? `共找到 ${results.length} 个相关结果，可按类别继续筛选`
              : "输入影视标题、主演或题材关键词，例如“悬疑”“访谈”“深海”"}
          </p>

          <form onSubmit={submit} className="mt-5 flex max-w-2xl flex-col gap-2 sm:flex-row">
            <div className="flex h-11 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 transition-colors focus-within:border-primary-400">
              <i className="ri-search-line flex h-4 w-4 items-center justify-center text-foreground-500" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索影视标题、导演、主演"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {input ? (
                <button
                  type="button"
                  onClick={() => setInput("")}
                  className="flex h-5 w-5 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
                  aria-label="清空输入"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              ) : null}
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-[13px] font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="font-label text-[11px] text-foreground-500">热门搜索</span>
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => setParams({ q: word })}
                className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="w-full px-4 py-8 md:px-6 md:py-10">
        {keyword ? (
          <>
            <div className="mb-5 flex flex-wrap items-center gap-1 rounded-full border border-background-300/70 bg-background-50 p-1 sm:w-fit">
              {typeFilters.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setTypeFilter(tab.key)}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[12px] transition-colors ${
                    typeFilter === tab.key
                      ? "bg-primary-500 font-medium text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {tab.label}
                  <span
                    className={`font-label text-[10px] ${
                      typeFilter === tab.key ? "text-background-50/70" : "text-foreground-400"
                    }`}
                  >
                    {counts[tab.key] ?? 0}
                  </span>
                </button>
              ))}
            </div>

            {results.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-14 text-center">
                <i className="ri-emotion-sad-line mx-auto flex h-12 w-12 items-center justify-center text-3xl text-foreground-400" />
                <h2 className="mt-4 font-heading text-base font-semibold text-foreground-900">
                  没有找到与“{keyword}”相关的内容
                </h2>
                <p className="mt-2 text-[13px] text-foreground-600">换个关键词试试，或看看下面的热门影视推荐</p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setInput("");
                      setParams({});
                    }}
                    className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-[13px] font-medium text-background-50 transition-colors hover:bg-accent-600"
                  >
                    <i className="ri-restart-line flex h-4 w-4 items-center justify-center" />
                    重新搜索
                  </button>
                  <Link
                    to="/"
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-[13px] text-foreground-800 transition-colors hover:border-accent-300 hover:text-accent-600"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <p className="mb-4 flex items-center gap-2 font-label text-[11px] text-foreground-500">
                  <i className="ri-list-check flex h-3.5 w-3.5 items-center justify-center text-accent-600" />
                  当前展示 {results.length} 个结果
                  {typeFilter !== "all" ? ` · 已筛选 ${typeLabels[typeFilter as ContentType]}` : ""}
                </p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5 xl:grid-cols-6">
                  {results.map((item) => (
                    <MediaCard key={item.id} item={item} showType />
                  ))}
                </div>
              </>
            )}

            <section className="mt-12">
              <h2 className="font-heading text-lg font-bold text-foreground-950">大家都在看</h2>
              <p className="mt-1.5 text-[12px] text-foreground-500">按热度排序的热门影视，也可以换这些关键词重新搜索</p>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
                {hotPicks.map((item) => (
                  <MediaCard key={item.id} item={item} showType />
                ))}
              </div>
            </section>
          </>
        ) : (
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950">热门影视推荐</h2>
            <p className="mt-1.5 text-[12px] text-foreground-500">现在最受欢迎的电影、剧集与综艺</p>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
              {hotPicks.map((item) => (
                <MediaCard key={item.id} item={item} showType />
              ))}
            </div>
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {(["movie", "tv", "variety"] as ContentType[]).map((type) => (
                <Link
                  key={type}
                  to={`/category/${type}`}
                  className="group flex items-center justify-between rounded-lg border border-background-200 bg-background-100/60 px-4 py-4 transition-colors hover:border-accent-300"
                >
                  <div>
                    <p className="font-heading text-sm font-semibold text-foreground-950">
                      浏览全部{typeLabels[type]}
                    </p>
                    <p className="mt-1 font-label text-[11px] text-foreground-500">
                      {mediaList.filter((i) => i.type === type).length} 部在库作品
                    </p>
                  </div>
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-background-50 text-accent-600 transition-transform group-hover:translate-x-1">
                    <i className="ri-arrow-right-line text-sm" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}